import type {
  BridgeHandlerMap,
  NativeWrapperMessage,
} from './native-bridge/types';
import { handleAppsflyerConversionData } from './native-bridge/handlers/appsflyer-conversion-data';
import { handleAppsflyerUid } from './native-bridge/handlers/appsflyer-uid';
import { handleFirebaseIdentity } from './native-bridge/handlers/firebase-identity';
import { handleSmarticoDeepLink } from './native-bridge/handlers/smartico-deep-link';
import { handleSmarcticoPushToken } from './native-bridge/handlers/smartico-push-token';
import { NativeEvent } from '~/shared/NativeBridge';

/**
 * Ponte com o app nativo (RN WebView). No-op fora da WebView.
 *
 * Para adicionar um novo tipo de mensagem:
 *   1. Adicione a entrada na union `NativeWrapperMessage` em native-bridge/types.ts
 *   2. Crie o handler em native-bridge/handlers/
 *   3. Registre no mapa abaixo — o TypeScript recusa compilar se faltar
 *   4. Adicione o case em `dispatch` — o `never` no default garante exaustividade
 */

const handlers: BridgeHandlerMap = {
  smarticoPushToken: handleSmarcticoPushToken,
  smarticoDeepLink: handleSmarticoDeepLink,
  appsflyerUid: handleAppsflyerUid,
  firebaseIdentity: handleFirebaseIdentity,
  appsflyerConversionData: handleAppsflyerConversionData,
};

function dispatch(message: NativeWrapperMessage): void {
  switch (message.type) {
    case 'smarticoPushToken':
      handlers.smarticoPushToken(message.payload);
      break;

    case 'smarticoDeepLink':
      handlers.smarticoDeepLink(message.payload);
      break;

    case 'appsflyerUid':
      handlers.appsflyerUid(message.payload);
      break;

    case 'firebaseIdentity':
      handlers.firebaseIdentity(message.payload);
      break;

    case 'appsflyerConversionData':
      handlers.appsflyerConversionData(message.payload);
      break;

    default:
      message satisfies never;
      break;
  }
}

function parseMessage(data: unknown): NativeWrapperMessage | null {
  if (typeof data !== 'string') {
    return null;
  }

  try {
    const parsed = JSON.parse(data);

    if (parsed && typeof parsed.type === 'string') {
      return parsed as NativeWrapperMessage;
    }
  } catch {
    // não é JSON, ignora silenciosamente — outros consumidores podem usar
  }

  return null;
}

export default defineNuxtPlugin(() => {
  if (!NativeEvent.isNativeApp()) {
    return;
  }

  window.addEventListener('message', (event) => {
    const message = parseMessage(event.data);

    if (!message) {
      return;
    }

    dispatch(message);
  });

  // Pede replay do token caso o app tenha enviado antes do mount.
  try {
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: 'webPageReady' }),
    );
  } catch {
    // sem ponte
  }
});
